import { GroupBy } from '@/modules/common/types';
import { ProjectStatus } from '@/modules/projects/types/project';
import { logger } from '@/utils/logger/logger';
import { urlParamUtils } from '@/utils/storage/url-local-storage';

import {
  PLANNER_AVAILABILITY_FILTER_KEY,
  PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY,
  PLANNER_DATE_RANGE_FILTER_KEY,
  PLANNER_PROJECT_PROBABILITY_FILTER_KEY,
  PLANNER_PROJECT_STATUS_FILTER_KEY,
} from '../../constants/filter-keys';
import {
  REORDERABLE_USER_INFO_COLUMN_KEYS,
  UserInfoColumnKey,
} from '../../constants/user-info-columns';
import { AssignmentStatus } from '../../types/assignment-status';
import { UtilizationBasedOn } from '../../types/utilization-based-on';
import { PlannerPreferences } from '../../types/utilization-filters';
import { dropOrphanedFilters, hasFilterSelection } from '../helpers/filter-cascades';
import { PlannerFilters, PlannerStore, UserInfoVisibility } from '../types';
import { PlannerPersistedState } from './persisted-state';
import { DEFAULT_USER_INFO_VISIBILITY } from './user-info-defaults';

const TAG = 'PlannerStoreHydration:';

/**
 * Hydration of the persisted planner state. Priority: URL params > localStorage
 * > defaults. The URL is authoritative for shareable links: when it contains ANY
 * known planner param, absent filter params mean "intentionally empty filters"
 * (not "fall back to localStorage") — otherwise stale filters would reappear
 * after a user clears them and refreshes.
 */

/** Persisted/URL params that are NOT filter values. Everything else in the URL is a filter. */
const KNOWN_NON_FILTER_PARAMS = [
  'zoomLevel',
  'collapsedGroups',
  'groupBy',
  'utilizationBasedOn',
  'assignmentStatuses',
  'showFilteredOutAssignments',
  'ignorePlaceholdersForGroup',
];

/**
 * Extracts filter values from URL params by exclusion: any param not in
 * KNOWN_NON_FILTER_PARAMS is a filter. Comma-separated values become arrays;
 * 'undefined' artifacts (zustand serialization of undefined) are dropped.
 */
export const parseUrlFilters = (urlParams: URLSearchParams): PlannerFilters => {
  const urlFilters: PlannerFilters = {};

  urlParams.forEach((value, key) => {
    if (KNOWN_NON_FILTER_PARAMS.includes(key)) return;

    if (value.includes(',')) {
      const cleanValues = value.split(',').filter(v => v && v !== 'undefined');
      if (cleanValues.length > 0) {
        urlFilters[key] = cleanValues;
      }
    } else if (value && value !== 'undefined') {
      urlFilters[key] = value;
    }
  });

  return urlFilters;
};

/**
 * Availability filters require the date range (backend code 2011); probability
 * requires projectStatus to include TENTATIVE. Drop either when its gate is
 * missing so a stale value can't hydrate as a locked chip.
 */
export const sanitizeGatedFilters = (filters: PlannerFilters): PlannerFilters => {
  const hasHydratedDateRange = hasFilterSelection(filters[PLANNER_DATE_RANGE_FILTER_KEY]);
  const projectStatusValue = filters[PLANNER_PROJECT_STATUS_FILTER_KEY];
  const hasHydratedTentativeStatus = (
    Array.isArray(projectStatusValue)
      ? projectStatusValue
      : projectStatusValue
        ? [projectStatusValue]
        : []
  ).includes(ProjectStatus.Tentative);

  return dropOrphanedFilters(
    dropOrphanedFilters(
      filters,
      [PLANNER_AVAILABILITY_FILTER_KEY, PLANNER_BILLABLE_AVAILABILITY_FILTER_KEY],
      hasHydratedDateRange
    ),
    [PLANNER_PROJECT_PROBABILITY_FILTER_KEY],
    hasHydratedTentativeStatus
  );
};

// Legacy pre-refactor storage keys for the member-info columns. These used to be
// hand-rolled localStorage entries next to the zustand persist entry; they now
// live inside 'planner-preferences'. Read as a fallback until the new entry
// contains them, then deleted.
const LEGACY_USER_INFO_VISIBILITY_STORAGE_KEY = 'planner-user-info-visibility';
const LEGACY_USER_INFO_ORDER_STORAGE_KEY = 'planner-user-info-order';

const readLegacyUserInfoVisibility = (): Partial<UserInfoVisibility> | null => {
  try {
    const stored = localStorage.getItem(LEGACY_USER_INFO_VISIBILITY_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const readLegacyUserInfoOrder = (): UserInfoColumnKey[] | null => {
  try {
    const stored = localStorage.getItem(LEGACY_USER_INFO_ORDER_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((key): key is string => typeof key === 'string');
  } catch {
    return null;
  }
};

const removeLegacyKey = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/**
 * Resolves userInfoVisibility: new persisted entry > legacy localStorage key >
 * defaults. The legacy key is deleted only once the value has landed in the new
 * entry — deleting it earlier would lose the preference if the user never
 * triggers a persist write before the next reload.
 */
const resolveUserInfoVisibility = (
  persisted: PlannerPersistedState | undefined
): UserInfoVisibility => {
  const source = persisted?.userInfoVisibility ?? readLegacyUserInfoVisibility();
  if (persisted?.userInfoVisibility) {
    removeLegacyKey(LEGACY_USER_INFO_VISIBILITY_STORAGE_KEY);
  }
  if (!source) return DEFAULT_USER_INFO_VISIBILITY;
  return {
    ...DEFAULT_USER_INFO_VISIBILITY,
    ...source,
    customAttributes: { ...(source.customAttributes ?? {}) },
  };
};

/**
 * Resolves userInfoOrder with the same priority + delayed legacy cleanup as
 * resolveUserInfoVisibility. A stale/hand-edited value is reconciled against the
 * live column keys later on read (see reconcileUserInfoOrder), so it can never
 * break rendering.
 */
const resolveUserInfoOrder = (
  persisted: PlannerPersistedState | undefined
): UserInfoColumnKey[] => {
  const persistedOrder = Array.isArray(persisted?.userInfoOrder)
    ? persisted.userInfoOrder.filter((key): key is string => typeof key === 'string')
    : null;
  if (persistedOrder) {
    removeLegacyKey(LEGACY_USER_INFO_ORDER_STORAGE_KEY);
    return persistedOrder;
  }
  return readLegacyUserInfoOrder() ?? [...REORDERABLE_USER_INFO_COLUMN_KEYS];
};

/**
 * zustand persist `merge`: builds the hydrated state from URL params (highest
 * priority), the persisted localStorage entry, then the in-memory defaults.
 * `persistedState` is undefined on a first visit (zustand v5 always calls merge).
 * Any throw here would be silently swallowed by zustand and drop the WHOLE
 * hydration — hence the explicit try/catch fallback to defaults.
 */
export const mergePersistedPlannerState = (
  persistedState: unknown,
  currentState: PlannerStore
): PlannerStore => {
  try {
    const persisted = persistedState as PlannerPersistedState | undefined;

    const urlZoomLevel = urlParamUtils.get('zoomLevel');
    const urlCollapsedGroups = urlParamUtils.get('collapsedGroups');
    const urlGroupBy = urlParamUtils.get('groupBy') as GroupBy | null;
    const urlUtilizationBasedOn = urlParamUtils.get(
      'utilizationBasedOn'
    ) as UtilizationBasedOn | null;
    const urlIgnorePlaceholders = urlParamUtils.get('ignorePlaceholdersForGroup');
    const urlAssignmentStatuses = urlParamUtils.get('assignmentStatuses');

    const urlParams = urlParamUtils.getAll();
    const urlFilters = parseUrlFilters(urlParams);

    // Preferences: URL params (highest priority) > persisted > current state
    const persistedOrCurrentPreferences =
      persisted?.plannerPreferences ?? currentState.plannerPreferences;
    const plannerPreferences: PlannerPreferences = {
      ...persistedOrCurrentPreferences,
      ...(urlUtilizationBasedOn !== null && { utilizationBasedOn: urlUtilizationBasedOn }),
      ...(urlAssignmentStatuses && {
        assignmentStatuses: urlAssignmentStatuses
          .split(',')
          .filter(Boolean)
          .map(status => status as AssignmentStatus),
      }),
      ...(urlIgnorePlaceholders !== null && {
        ignorePlaceholdersForGroup: urlIgnorePlaceholders === 'true',
      }),
    };

    // If the URL contains any known planner params (e.g. groupBy, zoomLevel),
    // it was set by our app — treat the absence of filter params as intentionally
    // empty filters rather than falling back to localStorage. This prevents stale
    // filters from reappearing after a user clears them and refreshes the page.
    const resolvedFilters =
      Object.keys(urlFilters).length > 0
        ? urlFilters
        : urlParams.has('groupBy') || urlParams.has('zoomLevel')
          ? {}
          : (persisted?.filters ?? currentState.filters);

    return {
      ...currentState,
      zoomLevel:
        urlZoomLevel !== null
          ? Number(urlZoomLevel)
          : (persisted?.zoomLevel ?? currentState.zoomLevel),
      collapsedGroups:
        urlCollapsedGroups !== null
          ? new Set(urlCollapsedGroups.split(',').filter(Boolean))
          : persisted?.collapsedGroups
            ? new Set(persisted.collapsedGroups)
            : currentState.collapsedGroups,
      filters: sanitizeGatedFilters(resolvedFilters),
      groupBy: urlGroupBy !== null ? urlGroupBy : (persisted?.groupBy ?? currentState.groupBy),
      plannerPreferences,
      userInfoVisibility: resolveUserInfoVisibility(persisted),
      userInfoOrder: resolveUserInfoOrder(persisted),
    };
  } catch (error) {
    logger.error(TAG, 'Failed to hydrate planner preferences, falling back to defaults', {
      error,
    });
    return currentState;
  }
};
